import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Stat {
  value: string;
  label: string;
}

export default function HeroSection() {
  const { t } = useTranslation();
  const stats = t('home.stats', { returnObjects: true }) as Stat[];

  return (
    <section className="relative overflow-hidden bg-foreground-950">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(oklch(var(--background-50)) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--background-50)) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />
      <div className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-primary-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-accent-500/15 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-content grid-cols-1 items-center gap-12 px-4 pb-16 pt-32 md:px-6 md:pb-24 md:pt-40 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-6">
          <span
            className="inline-flex items-center gap-2 rounded-full border border-primary-400/40 bg-primary-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary-300 animate-fade-in"
          >
            <i className="ri-fire-line text-sm" />
            {t('home.hero.badge')}
          </span>

          <h1
            className="mt-6 text-4xl font-bold leading-[1.08] text-background-50 animate-fade-up md:text-6xl"
            style={{ animationDelay: '80ms' }}
          >
            {t('home.hero.titleLine1')}{' '}
            <span className="text-primary-400">{t('home.hero.titleHighlight')}</span>
          </h1>

          <p
            className="mt-6 max-w-xl text-base leading-relaxed text-background-200 animate-fade-up md:text-lg"
            style={{ animationDelay: '160ms' }}
          >
            {t('home.hero.subtitle')}
          </p>

          <div
            className="mt-9 flex flex-col gap-3 animate-fade-up sm:flex-row sm:items-center"
            style={{ animationDelay: '240ms' }}
          >
            <Link
              to="/processing"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600"
            >
              {t('home.hero.ctaPrimary')}
              <i className="ri-arrow-right-line text-base" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/25 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-background-50/10"
            >
              {t('home.hero.ctaSecondary')}
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-background-200">
            <span className="flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-lg text-accent-400" />
              {t('home.intro.point1')}
            </span>
            <span className="flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-lg text-accent-400" />
              {t('home.intro.point2')}
            </span>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="relative mx-auto max-w-xl">
            <div className="absolute -left-4 -top-4 h-24 w-24 rounded-lg border border-primary-400/40" />
            <div className="relative overflow-hidden rounded-lg border border-background-50/10">
              <div className="h-64 w-full sm:h-80 lg:h-[24rem]">
                <img
                  src="https://www.sn-metall.de/mediapool/126/1269216/resources/56586064.jpg"
                  alt="Gesenkbiegepresse in der Blechverarbeitung bei S+N Metall"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 left-6 flex items-center gap-3 rounded-lg border border-background-50/10 bg-foreground-900/90 px-5 py-4 backdrop-blur-sm">
              <i className="ri-award-line text-2xl text-primary-400" />
              <div className="leading-tight">
                <p className="font-heading text-lg font-bold text-background-50">20+</p>
                <p className="text-[11px] uppercase tracking-[0.14em] text-background-200">
                  {stats[1].label}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}